import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, PaperPlaneTilt, CheckCircle, X, Truck,
  Warehouse, MapPin, MagnifyingGlass, FilePdf, Package,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { STATUS_PILL, DIR_META, Field, dirKey } from "@/pages/warehouse/_movementShared";

// =========================================================================
// EndpointPicker — warehouse / site + entity dropdown (used only by TransferSheet)
// =========================================================================
function EndpointPicker({ label, value, onChange, warehouses, projects, disabled, testId }) {
  const opts = value.kind === "warehouse" ? warehouses : projects;
  return (
    <div className="border border-slate-200 rounded-sm p-3" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">{label}</div>
      <div className="flex gap-2 mb-2">
        <Tabs value={value.kind} onValueChange={(k) => onChange({ kind: k, id: "", name: "" })}>
          <TabsList className="h-8">
            <TabsTrigger value="warehouse" disabled={disabled} data-testid={`${testId}-kind-warehouse`} className="text-[11px]"><Warehouse size={11} className="mr-1" /> Warehouse</TabsTrigger>
            <TabsTrigger value="site" disabled={disabled} data-testid={`${testId}-kind-site`} className="text-[11px]"><MapPin size={11} className="mr-1" /> Site</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <Select value={value.id} onValueChange={(id) => {
        const opt = opts.find((o) => o.id === id);
        onChange({ ...value, id, name: opt?.name || "" });
      }} disabled={disabled}>
        <SelectTrigger data-testid={`${testId}-select`}><SelectValue placeholder={`Pick ${value.kind}...`} /></SelectTrigger>
        <SelectContent>
          {opts.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// Variance cell renderer (helper extracted from render to remove nested ternary)
function VarianceCell({ line }) {
  if (line.variance_qty > 0) return <span className="text-amber-700 font-bold">{line.variance_qty}</span>;
  return line.received_qty != null ? "0" : "—";
}

export default function TransferSheet({ open, onClose, transfer, warehouses, projects, onSaved, onAction }) {
  const isEdit = !!transfer;
  const [form, setForm] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [stockSearch, setStockSearch] = useState("");
  const [receiveMode, setReceiveMode] = useState(false);
  const [receiveLines, setReceiveLines] = useState({});

  useEffect(() => {
    if (!open) return;
    if (transfer) {
      setForm({ ...transfer });
    } else {
      setForm({
        source: { kind: "warehouse", id: "", name: "" },
        destination: { kind: "site", id: "", name: "" },
        transfer_date: new Date().toISOString().slice(0, 10),
        lines: [],
        vehicle_no: "", driver_name: "", driver_phone: "",
        transport_mode: "road", e_way_bill_no: "",
        reason: "", notes: "",
      });
    }
    setReceiveMode(false); setReceiveLines({});
  }, [open, transfer]);

  useEffect(() => {
    if (!form?.source?.id || !form?.source?.kind || transfer) return;
    (async () => {
      try {
        const q = new URLSearchParams({ source_kind: form.source.kind, source_id: form.source.id, search: stockSearch });
        const { data } = await api.get(`/material-transfers/source/stock?${q.toString()}`);
        setStockItems(data.items || []);
      } catch (e) {
        if (typeof console !== "undefined") console.debug("[MaterialMovement] stock fetch skipped:", e?.response?.status || e?.message);
      }
    })();
  }, [form?.source?.id, form?.source?.kind, stockSearch, transfer]);

  if (!open || !form) return null;

  const optionsForKind = (k) => (k === "warehouse" ? warehouses : projects);
  const nameForKindId = (k, id) => {
    const opts = optionsForKind(k);
    return opts.find((x) => x.id === id)?.name || "";
  };

  const addLine = (item) => {
    if (form.lines.some((l) => l.item_id === item.id)) {
      toast.info("Item already added");
      return;
    }
    setForm((f) => ({
      ...f,
      lines: [...(f.lines || []), {
        item_id: item.id,
        name: item.name, sku: item.sku || "", unit: item.unit || "nos",
        qty: 1, unit_cost: item.unit_cost || 0, hsn: item.hsn || "",
        remarks: "",
      }],
    }));
  };

  const updateLine = (idx, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  };

  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.source.id || !form.destination.id) {
      toast.error("Pick both source and destination");
      return;
    }
    if (form.source.kind === form.destination.kind && form.source.id === form.destination.id) {
      toast.error("Source and destination must differ");
      return;
    }
    if (!(form.lines || []).length) {
      toast.error("Add at least one line item");
      return;
    }
    try {
      const payload = {
        ...form,
        source: { ...form.source, name: form.source.name || nameForKindId(form.source.kind, form.source.id) },
        destination: { ...form.destination, name: form.destination.name || nameForKindId(form.destination.kind, form.destination.id) },
      };
      if (isEdit) {
        await api.put(`/material-transfers/${transfer.id}`, payload);
      } else {
        await api.post("/material-transfers", payload);
      }
      toast.success(isEdit ? "Transfer updated" : "Draft created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const doReceive = async () => {
    const linesPayload = Object.entries(receiveLines).map(([key, v]) => ({
      name: key.split("|")[0], sku: key.split("|")[1] || "",
      received_qty: parseFloat(v.received_qty || 0),
      variance_reason: v.variance_reason || "",
    }));
    await onAction(transfer.id, "receive", { lines: linesPayload });
    setReceiveMode(false);
  };

  const openMtnPdf = () => {
    window.open(`${api.defaults.baseURL}/material-transfers/${transfer.id}/mtn-pdf`, "_blank");
  };

  const readOnly = isEdit && form.status !== "draft";

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[min(1100px,100vw)] sm:max-w-none overflow-y-auto" data-testid="mm-sheet">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            {isEdit ? (
              <>
                <span data-testid="mm-sheet-title">{form.transfer_no}</span>
                <span className={`px-2 py-0.5 rounded-sm text-[11px] font-semibold border ${STATUS_PILL[form.status]}`} data-testid="mm-sheet-status">{(form.status || "").replace("_", " ")}</span>
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold ${DIR_META[dirKey(form)]?.cls || ""}`}>{DIR_META[dirKey(form)]?.label}</span>
              </>
            ) : (
              <>New Material Transfer</>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          {/* --- Endpoints --- */}
          <div className="grid grid-cols-2 gap-4">
            <EndpointPicker
              label="FROM (Source)"
              value={form.source}
              onChange={(v) => setForm((f) => ({ ...f, source: v, lines: [] }))}
              warehouses={warehouses}
              projects={projects}
              disabled={readOnly}
              testId="mm-src"
            />
            <EndpointPicker
              label="TO (Destination)"
              value={form.destination}
              onChange={(v) => setForm((f) => ({ ...f, destination: v }))}
              warehouses={warehouses}
              projects={projects}
              disabled={readOnly}
              testId="mm-dst"
            />
          </div>

          {/* --- Transport metadata --- */}
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/40">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Truck size={12} /> Transport & Compliance</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Transfer Date">
                <Input type="date" value={form.transfer_date || ""} onChange={(e) => setForm((f) => ({ ...f, transfer_date: e.target.value }))} disabled={readOnly} data-testid="mm-date" />
              </Field>
              <Field label="Vehicle No">
                <Input value={form.vehicle_no || ""} onChange={(e) => setForm((f) => ({ ...f, vehicle_no: e.target.value }))} placeholder="HR26 AB 1234" disabled={readOnly} data-testid="mm-vehicle" />
              </Field>
              <Field label="Driver Name">
                <Input value={form.driver_name || ""} onChange={(e) => setForm((f) => ({ ...f, driver_name: e.target.value }))} disabled={readOnly} data-testid="mm-driver" />
              </Field>
              <Field label="Driver Phone">
                <Input value={form.driver_phone || ""} onChange={(e) => setForm((f) => ({ ...f, driver_phone: e.target.value }))} placeholder="+91 …" disabled={readOnly} data-testid="mm-driver-phone" />
              </Field>
              <Field label="E-Way Bill">
                <Input value={form.e_way_bill_no || ""} onChange={(e) => setForm((f) => ({ ...f, e_way_bill_no: e.target.value }))} disabled={readOnly} data-testid="mm-eway" />
              </Field>
              <Field label="Mode">
                <Select value={form.transport_mode || "road"} onValueChange={(v) => setForm((f) => ({ ...f, transport_mode: v }))} disabled={readOnly}>
                  <SelectTrigger data-testid="mm-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="road">Road</SelectItem>
                    <SelectItem value="rail">Rail</SelectItem>
                    <SelectItem value="courier">Courier</SelectItem>
                    <SelectItem value="hand-carry">Hand Carry</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reason" span={2}>
                <Input value={form.reason || ""} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Surplus return, reassignment, urgent supply..." disabled={readOnly} data-testid="mm-reason" />
              </Field>
            </div>
          </div>

          {/* --- Material picker (only when creating and source picked) --- */}
          {!readOnly && form.source.id && (
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="flex items-center gap-2 mb-2">
                <MagnifyingGlass size={13} className="text-slate-500" />
                <Input
                  value={stockSearch}
                  onChange={(e) => setStockSearch(e.target.value)}
                  placeholder={`Search stock at ${form.source.name || "source"}...`}
                  className="h-8 text-[12px]"
                  data-testid="mm-stock-search"
                />
              </div>
              <div className="max-h-40 overflow-y-auto text-[11.5px] space-y-0.5">
                {stockItems.length === 0 ? (
                  <div className="text-slate-400 py-2">No stock available at this source location.</div>
                ) : (
                  stockItems.slice(0, 30).map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => addLine(it)}
                      className="w-full flex items-center justify-between px-2 py-1 hover:bg-orange-50 rounded-sm text-left"
                      data-testid={`mm-add-stock-${it.id}`}
                    >
                      <span className="truncate">
                        <span className="font-medium">{it.name}</span>
                        {it.sku && <span className="text-slate-400 ml-2 font-mono text-[10px]">{it.sku}</span>}
                      </span>
                      <span className="font-mono text-slate-500 text-[10px]">{it.stock} {it.unit}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* --- Lines --- */}
          <div className="border border-slate-200 rounded-sm">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1"><Package size={12} /> Line Items ({(form.lines || []).length})</div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px]">Material</TableHead>
                  <TableHead className="text-[10px]">Unit</TableHead>
                  <TableHead className="text-[10px] text-right">Dispatched Qty</TableHead>
                  {isEdit && form.status !== "draft" && (
                    <>
                      <TableHead className="text-[10px] text-right">Received Qty</TableHead>
                      <TableHead className="text-[10px] text-right">Variance</TableHead>
                    </>
                  )}
                  <TableHead className="text-[10px] text-right">Unit Cost</TableHead>
                  {!readOnly && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(form.lines || []).map((l, i) => (
                  <TableRow key={`${l.item_id || l.name}-${i}`} data-testid={`mm-line-${i}`}>
                    <TableCell className="text-[12px]">
                      <div className="font-medium">{l.name}</div>
                      {l.sku && <div className="text-[10px] text-slate-400 font-mono">{l.sku}</div>}
                    </TableCell>
                    <TableCell className="text-[11.5px]">{l.unit}</TableCell>
                    <TableCell className="text-right">
                      {readOnly ? (
                        <span className="font-mono">{l.qty}</span>
                      ) : (
                        <Input
                          type="number" step="0.01" min="0.01"
                          value={l.qty}
                          onChange={(e) => updateLine(i, { qty: parseFloat(e.target.value || 0) })}
                          className="h-7 text-right text-[12px] w-24 inline-block"
                          data-testid={`mm-line-qty-${i}`}
                        />
                      )}
                    </TableCell>
                    {isEdit && form.status !== "draft" && (
                      <>
                        <TableCell className="text-right font-mono text-[12px]">{l.received_qty != null ? l.received_qty : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-[12px]"><VarianceCell line={l} /></TableCell>
                      </>
                    )}
                    <TableCell className="text-right font-mono text-[11.5px]">{fmtMoney(l.unit_cost)}</TableCell>
                    {!readOnly && (
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => removeLine(i)} className="h-6 w-6 p-0 text-red-500" data-testid={`mm-del-line-${i}`}>
                          <X size={11} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* --- Notes --- */}
          <Field label="Notes">
            <Textarea value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} disabled={readOnly} rows={2} data-testid="mm-notes" />
          </Field>

          {/* --- Receive mode UI --- */}
          {receiveMode && (
            <div className="border-2 border-emerald-300 bg-emerald-50 rounded-sm p-3" data-testid="mm-receive-panel">
              <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-800 mb-2 flex items-center gap-1"><CheckCircle size={12} /> Confirm Receipt</div>
              <div className="text-[11px] text-slate-600 mb-2">Enter received qty per line (defaults to dispatched qty). Short-receipts create variance records automatically.</div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {(form.lines || []).map((l) => {
                  const key = `${l.name}|${l.sku || ""}`;
                  const cur = receiveLines[key] || { received_qty: l.qty, variance_reason: "" };
                  return (
                    <div key={key} className="flex gap-2 items-center text-[11.5px]">
                      <span className="flex-1 truncate">{l.name}</span>
                      <span className="font-mono text-slate-500 text-[10px]">dispatched {l.qty}</span>
                      <Input
                        type="number" step="0.01" min="0"
                        value={cur.received_qty}
                        onChange={(e) => setReceiveLines((r) => ({ ...r, [key]: { ...cur, received_qty: e.target.value } }))}
                        className="h-7 w-24 text-right text-[12px]"
                        placeholder="qty"
                        data-testid={`mm-recv-qty-${l.name}`}
                      />
                      <Input
                        value={cur.variance_reason}
                        onChange={(e) => setReceiveLines((r) => ({ ...r, [key]: { ...cur, variance_reason: e.target.value } }))}
                        className="h-7 w-40 text-[11px]"
                        placeholder="short-receipt reason"
                        data-testid={`mm-recv-reason-${l.name}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-3">
                <Button onClick={doReceive} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mm-recv-confirm"><CheckCircle size={12} className="mr-1" /> Confirm Receipt</Button>
                <Button variant="outline" onClick={() => setReceiveMode(false)} className="rounded-sm">Cancel</Button>
              </div>
            </div>
          )}

          {/* --- Actions --- */}
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            {!isEdit && <Button onClick={save} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="mm-save-btn"><Plus size={13} className="mr-1" /> Create Draft</Button>}
            {isEdit && form.status === "draft" && (
              <>
                <Button onClick={save} variant="outline" className="rounded-sm" data-testid="mm-save-edit-btn">Save Edits</Button>
                <Button onClick={() => onAction(transfer.id, "dispatch")} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="mm-dispatch-btn"><PaperPlaneTilt size={13} className="mr-1" /> Dispatch</Button>
                <Button onClick={() => onAction(transfer.id, "cancel", { reason: "Draft cancelled" })} variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="mm-cancel-btn">Cancel</Button>
              </>
            )}
            {isEdit && form.status === "in_transit" && (
              <>
                <Button onClick={openMtnPdf} variant="outline" className="rounded-sm" data-testid="mm-mtn-pdf-btn"><FilePdf size={13} className="mr-1" /> MTN PDF</Button>
                <Button onClick={() => setReceiveMode(true)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mm-receive-btn"><CheckCircle size={13} className="mr-1" /> Receive</Button>
                <Button onClick={() => onAction(transfer.id, "cancel", { reason: "In-transit cancelled — reversing stock" })} variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="mm-cancel-transit-btn">Cancel & Reverse</Button>
              </>
            )}
            {isEdit && ["received", "partial_receipt"].includes(form.status) && (
              <>
                <Button onClick={openMtnPdf} variant="outline" className="rounded-sm" data-testid="mm-mtn-pdf-btn"><FilePdf size={13} className="mr-1" /> MTN PDF</Button>
                <Button onClick={() => onAction(transfer.id, "verify")} className="rounded-sm bg-purple-600 hover:bg-purple-700 text-white" data-testid="mm-verify-btn"><CheckCircle size={13} className="mr-1" /> Verify & Finalise</Button>
                <Button onClick={() => onAction(transfer.id, "cancel", { reason: "Post-receipt cancellation" })} variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="mm-cancel-post-btn">Cancel & Reverse</Button>
              </>
            )}
            {isEdit && ["verified", "cancelled"].includes(form.status) && (
              <Button onClick={openMtnPdf} variant="outline" className="rounded-sm" data-testid="mm-mtn-pdf-btn"><FilePdf size={13} className="mr-1" /> MTN PDF</Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
